import { fromJS } from 'immutable';
import localforage from 'localforage';
import { toJSON } from 'transit-immutable-js';

// https://github.com/robinhoodmarkets/rh/blob/8b68d46fa7b4d1c18b46b6c03da7f13c20565be3/web/shared/storage/src/constants/localStorage.ts#L633
export const LOCAL_STORAGE_AUTH_STATE_KEY = 'web:auth_state';
// https://github.com/robinhoodmarkets/rh/blob/8b68d46fa7b4d1c18b46b6c03da7f13c20565be3/web/shared/storage/src/constants/localForage.ts#L24
export const LOCAL_FORAGE_AUTH_STATE_KEY = 'reduxPersist:auth';

export type TokenScope = 'internal' | 'read' | 'web_limited' | 'loadtest';

/**
 * Result of POST to /oauth2/token with grant_type=refresh_token and on login
 * Sheriff does not generate types for this endpoint since they return polymorphic responses
 * Context: https://github.com/robinhoodmarkets/rh/blob/dab1924d/sheriff/sheriff/oauth2/views.py#L246
 */
export type SheriffTokenResponse = {
  access_token: string;
  expires_in: number;
  token_type: 'basic' | 'Bearer' | 'token';
  scope: TokenScope;
  refresh_token: string;
  mfa_code: null | string;
  backup_code: null | string;
  read_only_secondary_access_token?: string;
  user_uuid: string;
};

// Copied from https://github.com/robinhoodmarkets/rh/blob/b5ee7cab202cc95e8d57e64881314b768a7b2fc4/web/shared/auth/src/AuthPersistence.ts#L15
export const getAuthState = (): SheriffTokenResponse | null => {
  const localStorageAuthState = localStorage.getItem(LOCAL_STORAGE_AUTH_STATE_KEY);
  if (!localStorageAuthState) return null;
  try {
    const {
      access_token,
      backup_code,
      expires_in,
      mfa_code,
      read_only_secondary_access_token,
      refresh_token,
      scope,
      token_type,
      user_uuid,
    } = JSON.parse(localStorageAuthState);

    return {
      access_token,
      backup_code,
      expires_in,
      mfa_code,
      read_only_secondary_access_token,
      refresh_token,
      scope,
      token_type,
      user_uuid,
    };
  } catch {
    return null;
  }
};

export const setAuthState = async (tokenResponse: SheriffTokenResponse) => {
  localStorage.setItem(LOCAL_STORAGE_AUTH_STATE_KEY, JSON.stringify(tokenResponse));
  try {
    // Auth state is persisted to localforage in the form of an immutable map
    const tokenRefreshMap = fromJS(tokenResponse);
    await localforage.setItem(LOCAL_FORAGE_AUTH_STATE_KEY, JSON.stringify(toJSON(tokenRefreshMap)));
  } catch (err) {
    throw new Error('Error persisting auth to localforage');
  }
};

export const clearAuthState = async () => {
  localStorage.removeItem(LOCAL_STORAGE_AUTH_STATE_KEY);
  await localforage.removeItem(LOCAL_FORAGE_AUTH_STATE_KEY);
};
